import React, { useMemo, useState } from 'react'
import { Select } from '@surecompanies/core_components'
import dayjs from 'dayjs'
import { useDebounce } from 'hooks/useDebounce'

import { getShortText } from '../PaymentAssignment/Utils/payment-assignment.helper'

import {
  BenefitDefaultExpectedSource,
  BenefitInsuredPerson,
  BenefitSearchResult,
  useBenefitSearch,
} from '../../api/reconciliation-v2-api-client'

import { formatAmount } from './activity-log-helpers'
import styles from './BenefitSelect.module.scss'

const SEARCH_DEBOUNCE_MS = 300

/**
 * The year a result belongs to, from its effective date (fallback end date).
 * Null when neither is present — such rows are treated as in-year rather than
 * pushed into the "Other years" group.
 */
const resolveResultYear = (r: BenefitSearchResult): number | null => {
  const parsed = parseCalendarDate(r.benefitEffectiveDate ?? r.benefitEndDate)
  return parsed ? parsed.year() : null
}

/**
 * Benefit dates are calendar dates that arrive as UTC-midnight ISO strings.
 * Parse only the YYYY-MM-DD part so a browser west of UTC doesn't shift them
 * to the previous day (and a Jan 1 benefit into the prior year).
 */
const parseCalendarDate = (raw: string | null): dayjs.Dayjs | null => {
  if (!raw) return null
  const parsed = dayjs(raw.slice(0, 10))
  return parsed.isValid() ? parsed : null
}

const EXPECTED_SOURCE_LABEL: Record<BenefitDefaultExpectedSource, string> = {
  carrier: 'carrier',
  calculated: 'calculated',
  bundle: 'bundle',
}

/**
 * Muted second line of a search result: default expected (with its source),
 * shortened employee id, coverage window, carrier. Parts with no data are
 * omitted. Exported for unit tests.
 */
export const buildBenefitDetail = (r: BenefitSearchResult): string => {
  const expected = formatAmount(r.defaultExpected)
  const source = r.defaultExpectedSource
    ? ` (${EXPECTED_SOURCE_LABEL[r.defaultExpectedSource]})`
    : ''
  const start = parseCalendarDate(r.benefitEffectiveDate)
  const end = parseCalendarDate(r.benefitEndDate)
  const coverage =
    start || end
      ? `${start?.format('MM/DD/YYYY') ?? '—'}–${end?.format('MM/DD/YYYY') ?? 'present'}`
      : ''
  return [
    expected ? `Default expected ${expected}${source}` : '',
    r.employeePublicKey ? `Emp ID ${getShortText(r.employeePublicKey, 4)}` : '',
    coverage,
    r.carrierName ?? '',
  ]
    .filter(Boolean)
    .join(' · ')
}

/**
 * Hover text: the detail line plus what is too long for it — the full
 * employee id (copyable) and the company.
 */
export const buildBenefitTooltip = (
  r: BenefitSearchResult,
  detail: string
): string =>
  [
    detail,
    r.employeePublicKey ? `Employee ID ${r.employeePublicKey}` : '',
    r.companyName ?? '',
  ]
    .filter(Boolean)
    .join(' · ')

interface OptionDetail {
  detail: string
  tooltip: string
}

export interface BenefitOption {
  benefitId: string
  label: string
}

/**
 * Self-contained benefit picker that owns its own search query so each split
 * row can search independently (rules-of-hooks-safe — one hook per instance).
 * The `currentBenefit` and the active `value` are pinned into the option list
 * so the chosen label always renders even when it's not in the latest results.
 */
const BenefitSelect: React.FC<{
  value: BenefitOption | null
  onChange: (b: BenefitOption) => void
  /**
   * Allocation year (scopeYear). The picker searches a ±1-year window around it
   * (scopeYear-1 … scopeYear+1); exact-scopeYear results appear first and
   * adjacent-year results are grouped under "Other years", de-emphasized.
   */
  year?: number
  currentBenefit?: BenefitOption | null
  style?: React.CSSProperties
  placeholder?: string
  /**
   * Benefit IDs to hide from the options — used by the allocation drawer to
   * prevent picking the same benefit in two split groups. The picker's own
   * `value` is always shown even if it appears here (so the caller can safely
   * pass the union of all groups' benefits without special-casing self).
   */
  excludeBenefitIds?: ReadonlySet<string>
  /** Read-only gate — disables the picker for users without write access. */
  disabled?: boolean
}> = ({
  value,
  onChange,
  year,
  currentBenefit,
  style,
  placeholder,
  excludeBenefitIds,
  disabled,
}) => {
  const [q, setQ] = useState('')
  const debouncedQ = useDebounce(q, SEARCH_DEBOUNCE_MS)

  const isDebouncing = q.trim() !== debouncedQ.trim()
  const searchTerm = debouncedQ.trim()
  const anchorBenefitId = currentBenefit?.benefitId ?? value?.benefitId
  const {
    data: searchResults,
    isFetching: isSearching,
    isError: isSearchError,
  } = useBenefitSearch({
    // A typed name or benefit id searches everything in the year window. With
    // no term, list the anchor benefit's employee's other benefits instead
    // (the transaction's benefit, else the current pick); with no anchor
    // either, nothing is fetched.
    search: searchTerm || undefined,
    sameEmployeeAs: searchTerm ? undefined : anchorBenefitId,
    year,
    limit: 20,
  })

  // Merge: pinned current benefit first, then the active value (if not already
  // present), then live search results — de-duped by benefitId so the selected
  // label always has a matching option to render. Alongside the flat list we
  // track each option's year and whether it's pinned, so render can group
  // adjacent-year results without disturbing the pinned entries.
  const { options, yearById, pinnedIds, detailById } = useMemo(() => {
    const merged: BenefitOption[] = []
    const seen = new Set<string>()
    const optionYears = new Map<string, number | null>()
    const pinned = new Set<string>()
    const details = new Map<string, OptionDetail>()
    const selectedId = value?.benefitId
    const push = (
      b: BenefitOption | null | undefined,
      meta?: { pinned?: boolean; year?: number | null }
    ) => {
      if (!b || seen.has(b.benefitId)) return
      // Hide benefits already claimed by other groups, but never hide the
      // current picker's own selection so the chosen label always renders.
      if (
        excludeBenefitIds &&
        b.benefitId !== selectedId &&
        excludeBenefitIds.has(b.benefitId)
      ) {
        return
      }
      seen.add(b.benefitId)
      merged.push(b)
      optionYears.set(b.benefitId, meta?.year ?? null)
      if (meta?.pinned) pinned.add(b.benefitId)
    }
    // Show the roster entry the query actually matched (e.g. a searched-for
    // dependent), not just the primary — otherwise a hit whose match is a spouse
    // or child looks like it has nothing to do with the search. Falls back to
    // the primary [0] when there's no term or no entry matches.
    const tokens = debouncedQ.trim().toLowerCase().split(/\s+/).filter(Boolean)
    const matchedPerson = (
      persons: BenefitInsuredPerson[] | undefined
    ): BenefitInsuredPerson | undefined => {
      if (tokens.length === 0) return persons?.[0]
      return (
        persons?.find((p) => {
          const name = `${p.firstName} ${p.lastName}`.toLowerCase()
          return tokens.every((t) => name.includes(t))
        }) ?? persons?.[0]
      )
    }
    // Pinned entries carry no result payload, so their year is unknown — they
    // stay ungrouped at the top.
    push(currentBenefit, { pinned: true })
    push(value, { pinned: true })
    ;(searchResults ?? []).forEach((r) => {
      const insured = matchedPerson(r.insuredPersons)
      const who = insured
        ? `${insured.firstName} ${insured.lastName}`.trim()
        : ''
      const rel =
        insured && insured.relationship && insured.relationship !== 'employee'
          ? ` (${insured.relationship})`
          : ''
      const resultYear = resolveResultYear(r)
      // Insured person first (that's who the operator searched for), then the
      // employee when different, then the benefit id — carrier is dropped. The
      // year is appended so the operator can tell same-name benefits apart
      // across the ±1-year window.
      const base = [
        who ? `${who}${rel}` : r.employeeName,
        who && who !== r.employeeName ? r.employeeName : '',
        getShortText(r.benefitId, 4),
      ]
        .filter(Boolean)
        .join(' · ')
      const label = resultYear !== null ? `${base} (${resultYear})` : base
      push({ benefitId: r.benefitId, label }, { year: resultYear })
      // Detail lives beside the option, not in `label`: the label is reused as
      // the selected value and the pinned entry, which must stay one line.
      const detail = buildBenefitDetail(r)
      if (detail && !details.has(r.benefitId)) {
        details.set(r.benefitId, {
          detail,
          tooltip: buildBenefitTooltip(r, detail),
        })
      }
    })
    return {
      options: merged,
      yearById: optionYears,
      pinnedIds: pinned,
      detailById: details,
    }
  }, [currentBenefit, value, searchResults, excludeBenefitIds, debouncedQ])

  // Split results into the scopeYear bucket and an "Other years" group (only
  // when a year is set and off-year results exist). Pinned entries always lead,
  // ungrouped. The backend already orders exact-scopeYear rows first, so each
  // bucket keeps the API order — no extra client-side sort. When there's no
  // year or no off-year results this degrades to today's flat list.
  const antdOptions = useMemo(() => {
    // A pinned entry gets a detail line only when the results also contain it
    // (e.g. the same-employee list); otherwise it has no payload to show.
    const toOpt = (o: BenefitOption) => ({
      value: o.benefitId,
      label: o.label,
      ...detailById.get(o.benefitId),
    })
    const pinned: BenefitOption[] = []
    const exactYear: BenefitOption[] = []
    const offYear: BenefitOption[] = []
    options.forEach((o) => {
      if (pinnedIds.has(o.benefitId)) {
        pinned.push(o)
        return
      }
      const optionYear = yearById.get(o.benefitId) ?? null
      if (year !== undefined && optionYear !== null && optionYear !== year) {
        offYear.push(o)
        return
      }
      exactYear.push(o)
    })
    if (year === undefined || offYear.length === 0) {
      return [...pinned, ...exactYear, ...offYear].map(toOpt)
    }
    return [
      ...pinned.map(toOpt),
      ...(exactYear.length > 0
        ? [{ label: String(year), options: exactYear.map(toOpt) }]
        : []),
      {
        label: 'Other years',
        className: styles.offYearGroupLabel,
        options: offYear.map((o) => ({
          ...toOpt(o),
          className: styles.offYearOption,
        })),
      },
    ]
  }, [options, yearById, pinnedIds, detailById, year])

  const showSearching = isSearching || isDebouncing
  // Shown only when the dropdown has nothing to list. Searching and errors
  // win over an empty result; a typed query with no hits is "No matches";
  // an untyped employee list with no siblings is "No other benefits".
  let emptyMessage = 'Search by name or benefit ID'
  if (showSearching) emptyMessage = 'Searching…'
  else if (isSearchError) emptyMessage = 'Search failed. Try again.'
  else if (searchTerm) emptyMessage = 'No matches'
  else if (anchorBenefitId) emptyMessage = 'No other benefits'

  return (
    <Select
      showSearch
      disabled={disabled}
      placeholder={placeholder}
      style={style ?? { width: '100%' }}
      filterOption={false}
      value={value?.benefitId ?? undefined}
      onSearch={setQ}
      loading={showSearching}
      onChange={(val: string) => {
        const hit = options.find((o) => o.benefitId === val)
        onChange({ benefitId: val, label: hit?.label ?? val })
      }}
      options={antdOptions}
      optionRender={(opt) => {
        const { detail, tooltip } = opt.data as Partial<OptionDetail>
        if (!detail) return opt.label
        return (
          <div className={styles.option}>
            <div className={styles.optionTitle}>{opt.label}</div>
            <div className={styles.optionDetail} title={tooltip}>
              {detail}
            </div>
          </div>
        )
      }}
      notFoundContent={emptyMessage}
    />
  )
}

export default BenefitSelect
